import React, { useRef, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Icon from '@mui/material/Icon'
import CardMedia from '@mui/material/CardMedia'
import { useLocation, useNavigate } from 'react-router-dom'

import CustomSortTable from './CustomSortTable'
import { CircularProgress } from '@mui/material'
import { useMutation, useQueryClient } from 'react-query'
import { postDownloadReimbursementTxt } from 'services/api/modules/claim/approval-center/report'
import { downloadBlobFile } from 'services/helper/fileDownload'
import AdxInfoModal from 'components/reusable/modal/AdxInfoModal'
import { format } from 'date-fns'
import StepperAvatar from 'components/reusable/AdxProgressStepper/StepperAvatar'
import ClaimProgressTooltip from 'pages/approval-center/components/claim/ClaimProgressTooltip'
import defaultImage from 'assets/images/default_avatar.png'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'
import AdxButton from 'components/custom/AdxButton'

const PendingReimbursement = ({ reimburseData, error }) => {
  const reimbursementRequestColumn = [
    {
      checkbox: true,
      accessor: 'id',
    },
    {
      Header: 'Request',
      accessor: 'title',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
            columnGap={3}
          >
            <Box display="flex" alignItems="center" columnGap={2}>
              <Box
                display={'flex'}
                justifyContent={'center'}
                alignItems={'center'}
                width={50}
                height={50}
                bgcolor="alternate.light"
                borderRadius={'50%'}
              >
                <Icon sx={{ fontSize: 25, color: 'alternate.main' }}>article</Icon>
              </Box>
              <Box>
                <Typography
                  align="left"
                  width={{
                    xs: 'calc(100vw - 400px)',
                    lg: 'calc(100vw - 1200px)',
                  }}
                  minWidth={{ xs: 1, lg: '200px' }}
                  noWrap
                  variant="body1"
                  color="dark-text.main "
                  textTransform="capitalize"
                >
                  {data.title}
                </Typography>
                <Box display={'flex'} alignItems={'center'} columnGap={1.5}>
                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {data.claim_report_type}
                  </Typography>

                  <Icon sx={{ fontSize: 7, color: 'light-gray-text.main' }}>circle_rounded</Icon>

                  <Typography
                    noWrap
                    component="span"
                    variant="body2"
                    color="light-gray-text.main"
                    textTransform="capitalize"
                  >
                    {data.start_date} - {data.end_date}
                  </Typography>
                </Box>
                {data.in_payroll && (
                  <Typography align="left" variant="body2" color="alternate.main" noWrap>
                    {`Scheduled for payroll${data.payroll_period ? ` (${data.payroll_period})` : ''}`}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Reimburse amount',
      accessor: 'amount_payable',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Typography variant="body1" color="dark-text.main" align="left" noWrap>
            {`RM ${parseFloat(data.amount_payable).toFixed(2)}`}
          </Typography>
        )
      },
    },
    {
      Header: 'Requester',
      accessor: 'staff_name',
      Cell: (props) => {
        const data = props.row.original
        return (
          <Box display={'flex'} alignItems="center" columnGap={1}>
            <CardMedia
              component="img"
              sx={{
                height: 52,
                width: 52,
                bgcolor: 'gray-background.main',
                objectFit: 'cover',
                borderRadius: '50%',
                border: '3px solid',
                borderColor: 'white.main',
              }}
              alt={data.staff_name}
              src={data.staff_img || defaultImage}
            />

            <Typography variant="body1" color="dark-text.main" align="left" maxWidth={150} noWrap>
              {data.staff_name}
            </Typography>
          </Box>
        )
      },
    },
    {
      Header: 'Progress',
      accessor: 'report_status.name',
      Cell: (props) => {
        const data = props.row.original
        return (
          <ClaimProgressTooltip
            data={{
              members: data?.report_progress?.members,
              status: data?.report_status?.name,
            }}
          >
            <Box
              height="52px"
              width="52px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              position="relative"
            >
              <StepperAvatar
                name={data?.report_progress?.members}
                imgUrl={data?.report_progress?.profilePics}
                active
                sxAvatar={{ borderColor: 'white.main' }}
                disableToolTip
              />
            </Box>
          </ClaimProgressTooltip>
        )
      },
    },
  ]

  const navigate = useNavigate()
  const backgroundLocation = useLocation()
  const [selectedRow, setSelectedRow] = useState([])
  const [isErrorModalOpen, setErrorModalOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const containerRef = useRef()

  // convert string to txt and download it
  const downloadTxt = (txt) => {
    const fileName = `RCgen_Payment_DP_${format(new Date(), 'yyyyMMdd_HHmm')}`
    downloadBlobFile({
      data: txt,
      fileName: `${fileName}.csv`,
      contentType: 'text/plain',
      successMessage: 'Reimbursement CSV export downloaded.',
    })
  }

  // download txt API
  const queryClient = useQueryClient()
  const { isLoading: downloadIsLoading, mutate: mutateDownload } = useMutation(
    (values) => postDownloadReimbursementTxt(values),
    {
      onSuccess: async (res) => {
        downloadTxt(res)
        queryClient.invalidateQueries('reimbursePending')
        queryClient.invalidateQueries('reimburseDownloaded')
        setSelectedRow([])
      },
      onError: async (e) => {
        const test = JSON.parse(
          `{${e.data.split('{').splice(1, e.data.split('{').length).join('{')}`
        )
        setErrorMessage(test.message)
        handleErrorOpen()
      },
    }
  )

  const handleDownload = () => {
    const newArray = selectedRow.map((item) => item.id)
    const values = { report_ids: newArray }
    mutateDownload(values)
  }

  // error modal
  const handleErrorOpen = () => {
    setErrorModalOpen(true)
  }
  const handleErrorClose = () => {
    setErrorModalOpen(false)
  }

  return reimburseData.length > 0 ? (
    <Box
      display={'flex'}
      flexDirection={'column'}
      width={1}
      ref={containerRef}
      sx={{
        height: {
          xs: `calc(100vh - 180px)`,
          lg: 'calc(100vh - 185px)',
        },
        overflow: 'hidden',
      }}
    >
      <CustomSortTable
        stickyHeader
        nav="View"
        onClick={(e) => {
          navigate(`/app/approval-center/reimbursement/request/${e.id}`, {
            state: {
              backgroundLocation,
              data: e,
            },
          })
        }}
        column={reimbursementRequestColumn}
        data={reimburseData}
        sx={{
          height: {
            xs: `calc(100vh - 330px)`,
            lg: `calc(100vh - 240px)`,
          },
          boxShadow: null,
        }}
        selectedRow={selectedRow}
        setSelectedRow={setSelectedRow}
        // claims taken by a payroll run are paid on the payslip, not the bank file
        isRowSelectable={(row) => !row.in_payroll}
      />
      <Box
        borderTop={'1px solid'}
        borderColor={'grayscale.line-light'}
        bgcolor="primary.light"
        display="flex"
        flexDirection={{ xs: 'column', lg: 'row' }}
        justifyContent={{ xs: 'start', lg: 'space-between' }}
        rowGap={2}
        alignItems="center"
        height={{ xs: 150, lg: 100 }}
        width={{ xs: 1, lg: 1 }}
        position="sticky"
        bottom={0}
        right={0}
        sx={{ px: 3, py: 3, zIndex: 10 }}
      >
        <Typography>
          {selectedRow.length === 0
            ? 'Select at least one claim report to export'
            : `${selectedRow.length} claim report(s) selected`}
        </Typography>
        <AdxButton
          variant={'contained'}
          onClick={handleDownload}
          disableElevation
          disabled={selectedRow.length === 0}
        >
          {downloadIsLoading ? <CircularProgress sx={{ color: "#3A3A3A" }} size={16} /> : `Export as .csv`}
        </AdxButton>
      </Box>
      {/* ERROR MODAL */}
      <AdxInfoModal
        variant={'error'}
        open={isErrorModalOpen}
        onClose={(_, reason) => {
          if (reason !== 'backdropClick') {
            handleErrorClose()
          }
        }}
        handleClose={handleErrorClose}
        muiIcon="error"
        iconColor="error.main"
        title="An error occured"
        subtitle={errorMessage}
        footerButtons
        firstBtnLabel={'Cancel'}
        firstBtnHandle={handleErrorClose}
      ></AdxInfoModal>
    </Box>
  ) : (
    <AdxEmptyStateV2
      title={
        error?.status === 403
          ? 'You are not authorized to access this feature. Please contact your admin for access.'
          : 'No reimbursement request'
      }
    />
  )
}

export default PendingReimbursement
